import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";

import DatagridCustom from "./DatagridCustom";
import ActionHeaderCard from "./ActionHeaderCard";
import AppDialog from "./Dialog/AppDialog";
import ConfirmDialog from "./Dialog/ConfirmDialog";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import {
  getCustomStatuses,
  createCustomStatus,
  updateCustomStatus,
  disableCustomStatus,
  enableCustomStatus,
} from "../../services/CustomStatusService";

export default function ManagerCustomStatus({
  scope,
  title = "Estados del Sistema",
  description = "Administrar los estados propios del sistema. Cada estado tiene un código propio (definido por vos) para referenciarlo fácilmente, por ejemplo en una carga por Excel.",
  permission = "administrar estados personalizados",
}) {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(permission);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: [],
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formCode, setFormCode] = useState("");
  const [formName, setFormName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [toggleTarget, setToggleTarget] = useState(null);
  const [toggleLoading, setToggleLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const search = filterModel.quickFilterValues?.join(" ") || "";
    try {
      const res = await getCustomStatuses(scope, {
        perPage: paginationModel.pageSize,
        page: paginationModel.page + 1,
        search,
      });
      setRows(res.data?.data ?? []);
      setTotal(res.data?.total ?? 0);
    } catch {
      showToast("No se pudieron cargar los estados.", "error");
    } finally {
      setLoading(false);
    }
  }, [scope, paginationModel, filterModel, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setFormCode("");
    setFormName("");
    setError("");
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormCode(row.code || "");
    setFormName(row.name);
    setError("");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formCode.trim()) {
      setError("Completá el código.");
      return;
    }
    if (!formName.trim()) {
      setError("Completá el nombre.");
      return;
    }

    setSaving(true);
    setError("");
    const payload = { scope, code: formCode.trim(), name: formName.trim() };

    try {
      if (editing) {
        await updateCustomStatus(editing.id, payload);
      } else {
        await createCustomStatus(payload);
      }
      setDialogOpen(false);
      await load();
      showToast(editing ? "Estado actualizado." : "Estado creado.", "success");
    } catch (e) {
      const errors = e?.response?.data?.errors;
      const firstError = errors ? Object.values(errors)[0]?.[0] : null;
      setError(
        firstError || e?.response?.data?.message || "Error al guardar.",
      );
    } finally {
      setSaving(false);
    }
  };

  const openToggleConfirm = (row) => setToggleTarget(row);

  const handleToggle = async () => {
    if (!toggleTarget) return;

    setToggleLoading(true);
    try {
      if (toggleTarget.status === "enabled") {
        await disableCustomStatus(toggleTarget.id);
        showToast("Estado deshabilitado.", "success");
      } else {
        await enableCustomStatus(toggleTarget.id);
        showToast("Estado habilitado.", "success");
      }
      setToggleTarget(null);
      await load();
    } catch (e) {
      showToast(
        e?.response?.data?.message || "No se pudo cambiar el estado.",
        "error",
      );
    } finally {
      setToggleLoading(false);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.4 },
    { field: "code", headerName: "Código", flex: 0.6 },
    { field: "name", headerName: "Nombre", flex: 1.4 },
    {
      field: "status",
      headerName: "Estado",
      flex: 0.8,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value === "enabled" ? "Habilitado" : "Deshabilitado"}
          color={params.value === "enabled" ? "success" : "default"}
          variant="outlined"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Acciones",
      flex: 0.8,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton
            size="small"
            onClick={() => openEdit(params.row)}
            disabled={!canManage}
          >
            <EditRoundedIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color={params.row.status === "enabled" ? "error" : "success"}
            onClick={() => openToggleConfirm(params.row)}
            disabled={!canManage}
          >
            {params.row.status === "enabled" ? (
              <BlockRoundedIcon fontSize="small" />
            ) : (
              <CheckCircleOutlineRoundedIcon fontSize="small" />
            )}
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Grid container spacing={2} sx={{ mt: 4 }}>
      <Grid size={{ xs: 12 }}>
        <ActionHeaderCard
          icon={<TuneRoundedIcon />}
          title={title}
          description={description}
          action={
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={openCreate}
              disabled={!canManage}
              sx={{ borderRadius: 2, fontWeight: 800, textTransform: "none" }}
            >
              Agregar
            </Button>
          }
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <DatagridCustom
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          total={total}
          paginationModel={paginationModel}
          onPaginationChange={setPaginationModel}
          filterModel={filterModel}
          onFilterChange={(m) => {
            setFilterModel(m);
            setPaginationModel((p) => ({ ...p, page: 0 }));
          }}
          filterMode="server"
          isServerPagination
        />
      </Grid>

      <AppDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? "Editar estado" : "Nuevo estado"}
        maxWidth="xs"
        fullWidth
        stickyHeader
        actions={
          <>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{ borderRadius: 2, fontWeight: 900 }}
            >
              {saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "Guardar"
              )}
            </Button>
          </>
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
          {error && (
            <Box sx={{ color: "error.main", fontSize: 13, fontWeight: 700 }}>
              {error}
            </Box>
          )}
          {editing && (
            <Box sx={{ fontSize: 13, opacity: 0.7 }}>
              ID: <strong>{editing.id}</strong>
            </Box>
          )}
          <TextField
            label="Código"
            value={formCode}
            onChange={(e) => setFormCode(e.target.value)}
            size="small"
            helperText="Identificador corto para referenciar este estado (por ejemplo, desde una carga por Excel). Debe ser único dentro de este catálogo."
            autoFocus
          />
          <TextField
            label="Nombre"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            size="small"
            helperText="El nombre que se va a mostrar en el sistema."
          />
        </Box>
      </AppDialog>

      <ConfirmDialog
        open={!!toggleTarget}
        onClose={() => setToggleTarget(null)}
        onConfirm={handleToggle}
        title={
          toggleTarget?.status === "enabled"
            ? "Deshabilitar estado"
            : "Habilitar estado"
        }
        description={
          toggleTarget?.status === "enabled"
            ? `¿Deshabilitar "${toggleTarget?.name}"? No se elimina: los registros que ya lo tengan asignado lo van a seguir mostrando, pero no va a poder asignarse a registros nuevos.`
            : `¿Habilitar "${toggleTarget?.name}" nuevamente?`
        }
        confirmText={toggleLoading ? "Guardando..." : "Confirmar"}
        cancelText="Cancelar"
        confirmColor={toggleTarget?.status === "enabled" ? "error" : "success"}
        loading={toggleLoading}
      />
    </Grid>
  );
}
